/* eslint-disable max-len */
import { API_BASE_URL } from '../../utils/constants';
import HttpClient from '../../utils/http-client';

export interface SortCriteria {
  field: SortableFields;
  order: 'ASC' | 'DESC';
}

type SortableFields = string;

export interface AdminOverviewQueryPayload {
  page?: number;
  limit?: number;
  filters?: Filters;
  sort?: SortCriteria[];
}

interface Filters {
  assigned_user_ids?: number[];
  crm_status_ids?: number[];
  intents?: string[];
  audit_score_range?: {
    min: number;
    max: number;
  };
  opportunity_labels?: string[];
  meeting_source_ids?: number[];
  qualification_questions?: { 
    capture_id: string;
    value: any;
  }[];
}

class Customer {
  getAllCustomers = () => HttpClient.get(`${API_BASE_URL}/v1/customer`);

  getCustomer = id => HttpClient.get(`${API_BASE_URL}/v1/customer/${id}`);

  getCustomerPublic = id => HttpClient.get(`${API_BASE_URL}/v1/public/customer/${id}`);

  addCustomer = (data, workspaceId) =>
    HttpClient.post(
      `${API_BASE_URL}/v1/customer?workspaceId=${workspaceId}&source=ZIPTEAMS`,
      data,
    );

  editCustomer = data => HttpClient.put(`${API_BASE_URL}/v1/customer`, data);

  deleteCustomer = id => HttpClient.delete(`${API_BASE_URL}/v1/customer/${id}`);

  scheduleMeeting = (id, data) =>
    HttpClient.put(`${API_BASE_URL}/v1/customer/${id}/schedule`, data);

  cancelMeeting = (id, send_cancelled_event) =>
    HttpClient.delete(`${API_BASE_URL}/v1/customer/${id}/schedule?send_cancelled_event=${send_cancelled_event}`);

  cancelScheduledMeeting = (id, data, send_cancelled_event) =>
    HttpClient.delete(`${API_BASE_URL}/v1/customer/${id}/schedule/${data}?send_cancelled_event=${send_cancelled_event}`);

  getSearchSuggestions = (
    search_term: string,
    page_size: number,
    workspace_id: string,
  ) =>
    HttpClient.get(
      `${API_BASE_URL}/v1/customer/suggestions?page_size=${page_size}&search_term=${search_term}&workspace_id=${workspace_id}`,
    );

  getSearchSuggestionsWorkspace = (
    search_term: string,
    page_size: number,
    workspace_id: string,
    manager_id?: number
  ) =>
    HttpClient.get(
      `${API_BASE_URL}/v1/customer/suggestions/workspace/${workspace_id}?page_size=${page_size}&search_term=${search_term}${manager_id ? `&manager_id=${manager_id}` : ''}`,
    );

  getTrackedEntities = (customerId: string | string[]) =>
    HttpClient.get(
      `${API_BASE_URL}/v1/customer/${customerId}/tracked_entities`,
    );

  getSentimentOccurences = (customerId: string | string[], params) =>
    HttpClient.get(
      `${API_BASE_URL}/v1/customer/${customerId}/sentiment_occurences`,
      { params },
    );

  rateSentiment = (customerId: string | string[], occurenceId: string, data) =>
    HttpClient.post(
      `${API_BASE_URL}/v1/customer/${customerId}/sentiment_occurences/${occurenceId}/feedback`,
      data,
    );

  getCustomerRecommedations = (customerId: string | string[], params = {}) =>
    HttpClient.get(
      `${API_BASE_URL}/v1/customer/${customerId}/recommendations`,
      { params },
    );

  updateRecommendation = (
    customerId: string | string[],
    recommendationId: string,
    state: number,
  ) =>
    HttpClient.post(
      `${API_BASE_URL}/v1/customer/${customerId}/recommendation/${recommendationId}`,
      { state },
    );
  
  updateCustomerAssignee = (customerId: number, assigneeId: number) =>
    HttpClient.post(
      `${API_BASE_URL}/v1/customer/${customerId}/update-assignee/${assigneeId}`,
      {},
    );

  getCustomerLevelInsights = (customerId: number) => 
    HttpClient.get(
      `${API_BASE_URL}/v1/customer/${customerId}/customer-insights`
    );

  getCustomerLevelSuggestedActions = (customerId: number) => 
    HttpClient.get(
      `${API_BASE_URL}/v1/customer/${customerId}/action-items`
    );

  updateNextSteps = (customerId: number, payload: { conversation_id: number; action_label: string; is_completed: boolean }) => 
    HttpClient.post(
      `${API_BASE_URL}/v1/customer/${customerId}/customer-insights/update-nextsteps`, payload
    );

  updateActionItems = (customerId: number, actionItemId: number, payload: { is_completed: boolean }) => 
    HttpClient.patch(
      `${API_BASE_URL}/v1/customer/${customerId}/action-item/${actionItemId}`, payload
    );
  
  deleteContact = (contactId: number) =>
    HttpClient.delete(`${API_BASE_URL}/v1/customer/contact/${contactId}`);

  getOpportunityTypeList = (workspaceId: number) =>
    HttpClient.get(`${API_BASE_URL}/v1/customer/opportunity_types/${workspaceId}`);

  getAdminOverview = (workspaceId: number, payload: AdminOverviewQueryPayload) =>
    HttpClient.post(
      `${API_BASE_URL}/v1/customer/overview/workspace/${workspaceId}`,
      payload,
    );
  
  getCustomerIntentMetrics = (workspaceId: number, payload) =>
    HttpClient.post(
      `${API_BASE_URL}/v1/customer/status-graph/workspace/${workspaceId}`,
      payload
    )
    
}

const CustomerService = new Customer();
export default CustomerService;
